import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Plus, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api';
import RichTextEditor from '../components/shared/RichTextEditor';

export default function NewTicketPage() {
  const { user, isStaff } = useAuth();
  const navigate  = useNavigate();

  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [statuses,   setStatuses]   = useState([]);
  const [techs,      setTechs]      = useState([]);
  const [orgs,       setOrgs]       = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Watcher input state
  const [watcherInput, setWatcherInput] = useState('');
  const [watchers, setWatchers]         = useState([]); // [{email, name}]

  const [form, setForm] = useState({
    subject: '',
    description: '',
    category_id: '',
    priority_id: '',
    // Staff only
    status_id: '',
    assigned_to: '',
    org_id: '',
    initial_minutes: '',
    initial_minutes_notes: '',
    internal_note: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get('/lookups/categories').then(d => setCategories(d.categories || [])).catch(() => {});
    api.get('/lookups/priorities').then(d => setPriorities(d.priorities || [])).catch(() => {});
    if (isStaff) {
      api.get('/lookups/statuses').then(d => setStatuses(d.statuses || [])).catch(() => {});
      api.get('/users?role=tech,admin').then(d => setTechs((d.users || []).filter(u => ['admin','tech'].includes(u.role)))).catch(() => {});
      api.get('/orgs').then(d => setOrgs(d.orgs || [])).catch(() => {});
    }
  }, [isStaff]);

  const addWatcher = () => {
    const email = watcherInput.trim().toLowerCase();
    if (!email || !email.includes('@')) return toast.error('Enter a valid email address');
    if (watchers.find(w => w.email === email)) return toast.error('Already added');
    setWatchers(ws => [...ws, { email, name: '' }]);
    setWatcherInput('');
  };

  const removeWatcher = email => setWatchers(ws => ws.filter(w => w.email !== email));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) {
      return toast.error('Subject and description are required');
    }
    setSubmitting(true);
    try {
      const payload = {
        subject:      form.subject.trim(),
        description:  form.description.trim(),
        category_id:  form.category_id  || undefined,
        priority_id:  form.priority_id  || undefined,
      };
      if (isStaff) {
        if (form.status_id)             payload.status_id              = form.status_id;
        if (form.assigned_to)           payload.assigned_to            = form.assigned_to;
        if (form.org_id)                payload.org_id                 = form.org_id;
        if (form.internal_note.trim())  payload.internal_note          = form.internal_note.trim();
        if (form.initial_minutes)       payload.initial_minutes        = parseInt(form.initial_minutes) || 0;
        if (form.initial_minutes_notes) payload.initial_minutes_notes  = form.initial_minutes_notes.trim();
        if (watchers.length)            payload.watcher_emails         = watchers;
      }
      const { ticket } = await api.post('/tickets', payload);
      toast.success(`Ticket #${ticket.ticket_number} created`);
      navigate(`/tickets/${ticket.id}`);
    } catch (err) {
      toast.error(err.message || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div className="page-header">
        <h2>New Ticket</h2>
      </div>

      <div className="page-body">
        <form onSubmit={handleSubmit} style={{ maxWidth: 900, display: 'flex', flexDirection: 'column', gap: 0 }}>

          {/* ── Two-column layout for staff ── */}
          <div className="new-ticket-grid" style={{ display: 'grid', gridTemplateColumns: isStaff ? '1fr 320px' : '1fr', gap: 20, alignItems: 'start' }}>

            {/* Left: main ticket details */}
            <div className="card card-lg" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <h3 style={{ fontSize: 14, marginBottom: 16 }}>Ticket Details</h3>

              <div className="form-group">
                <label className="form-label">Subject *</label>
                <input
                  className="form-input"
                  placeholder="Briefly describe the issue"
                  value={form.subject}
                  onChange={e => set('subject', e.target.value)}
                  required
                  autoFocus
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-select" value={form.category_id} onChange={e => set('category_id', e.target.value)}>
                    <option value="">Select…</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select className="form-select" value={form.priority_id} onChange={e => set('priority_id', e.target.value)}>
                    <option value="">Select…</option>
                    {priorities.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Description *</label>
                <RichTextEditor
                  value={form.description}
                  onChange={v => set('description', v)}
                  placeholder="Describe the issue in detail. Include error messages, steps to reproduce, and what you've already tried."
                  minHeight={180}
                />
              </div>

              {/* Internal note — staff only, in main card */}
              {isStaff && (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Internal Note <span className="text-muted" style={{ fontWeight: 400 }}>(staff only)</span></label>
                  <textarea
                    className="form-textarea"
                    style={{ minHeight: 80, background: 'rgba(255,200,0,0.06)', borderColor: 'rgba(255,200,0,0.3)' }}
                    placeholder="Add an internal note visible only to staff…"
                    value={form.internal_note}
                    onChange={e => set('internal_note', e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* Right: staff-only sidebar */}
            {isStaff && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                {/* Assignment */}
                <div className="card card-lg">
                  <h3 style={{ fontSize: 13, marginBottom: 14 }}>Assignment</h3>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-select" value={form.status_id} onChange={e => set('status_id', e.target.value)}>
                      <option value="">Default (Open)</option>
                      {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Assign To</label>
                    <select className="form-select" value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
                      <option value="">Unassigned</option>
                      {techs.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Organization</label>
                    <select className="form-select" value={form.org_id} onChange={e => set('org_id', e.target.value)}>
                      <option value="">None</option>
                      {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  </div>
                </div>

                {/* Initial time entry */}
                <div className="card card-lg">
                  <div className="flex items-center gap-2" style={{ marginBottom: 14 }}>
                    <Clock size={14} style={{ color: 'var(--text-muted)' }} />
                    <h3 style={{ fontSize: 13 }}>Log Initial Time</h3>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Time Spent (minutes)</label>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="e.g. 30"
                      value={form.initial_minutes}
                      onChange={e => set('initial_minutes', e.target.value)}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Notes</label>
                    <input
                      className="form-input"
                      placeholder="What did you work on?"
                      value={form.initial_minutes_notes}
                      onChange={e => set('initial_minutes_notes', e.target.value)}
                    />
                  </div>
                </div>

                {/* Watchers */}
                <div className="card card-lg">
                  <h3 style={{ fontSize: 13, marginBottom: 14 }}>Watchers</h3>
                  <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
                    These people will receive ticket email notifications.
                  </p>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <input
                      className="form-input"
                      type="email"
                      placeholder="email@example.com"
                      value={watcherInput}
                      onChange={e => setWatcherInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addWatcher())}
                      style={{ flex: 1 }}
                    />
                    <button type="button" className="btn btn-secondary btn-sm" onClick={addWatcher}>
                      <Plus size={14} />
                    </button>
                  </div>
                  {watchers.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {watchers.map(w => (
                        <div key={w.email} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 8px', background: 'var(--bg-secondary)', borderRadius: 6, fontSize: 13 }}>
                          <span style={{ color: 'var(--text-secondary)' }}>{w.email}</span>
                          <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '2px 4px' }} onClick={() => removeWatcher(w.email)}>
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            )}
          </div>

          {/* Submit row */}
          <div className="flex gap-3" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting
                ? <><div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Creating…</>
                : 'Create Ticket'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}
